import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Leaf, ShieldCheck, Compass, ClipboardList, Filter, Thermometer, Droplets,
  Dna, Fish, PenTool, RefreshCw, CalendarCheck, Sprout, FlaskConical,
  Maximize, Waves, Box, ArrowRight, ArrowLeft, Check, ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const TRANSITION_DURATION_MS = 1500;

const WHY = [
  { icon: Leaf, title: "Natural Beauty", body: "Bring the tranquility of nature indoors with vibrant freshwater fish, lush aquatic plants, driftwood, and carefully curated aquascapes." },
  { icon: ShieldCheck, title: "Built to Last", body: "Every freshwater aquarium begins with a carefully engineered foundation designed for stability, healthy livestock, and long-term success." },
  { icon: Compass, title: "Expert Guidance", body: "Whether you're purchasing your first aquarium or redesigning an existing system, Seatech provides expert recommendations every step of the way." },
];

const STEPS = [
  { icon: ClipboardList, title: "System Planning", body: "Every successful aquarium begins by selecting the proper aquarium size, stand, location, and equipment based on your goals and available space." },
  { icon: Filter, title: "Professional Filtration", body: "Mechanical, biological, and chemical filtration are carefully selected to provide clean, stable water for healthy fish and thriving plants." },
  { icon: Thermometer, title: "Temperature Control", body: "Proper heating and climate management ensure a stable environment that supports tropical freshwater species." },
  { icon: Droplets, title: "Purified Water", body: "RODI water, premium conditioners, and carbon filtration create ideal water chemistry while reducing unwanted contaminants." },
  { icon: Leaf, title: "Aquascape Design", body: "Natural driftwood, decorative stone, substrate, and live aquatic plants are carefully arranged to create beautiful underwater landscapes." },
  { icon: Dna, title: "Biological Cycling", body: "The aquarium is fully cycled before introducing livestock, allowing beneficial bacteria to establish healthy biological filtration." },
  { icon: Fish, title: "Livestock Introduction", body: "Fish and aquatic plants are introduced gradually using professional acclimation techniques that reduce stress and promote long-term health." },
];

const SERVICES = [
  { icon: PenTool, title: "Custom Aquarium Design", body: "Professionally designed freshwater aquariums customized for homes, offices, businesses, and commercial environments." },
  { icon: RefreshCw, title: "Aquarium Redesign", body: "Transform outdated aquariums into beautiful modern aquatic displays using new layouts, equipment, plants, and livestock." },
  { icon: CalendarCheck, title: "Aquarium Maintenance", body: "Routine maintenance programs keep every aquarium clean, balanced, healthy, and visually stunning year-round." },
  { icon: Sprout, title: "Aquatic Plant Installation", body: "Professionally selected live plants that enhance beauty while contributing to a healthier ecosystem." },
  { icon: Fish, title: "Livestock Selection", body: "Expert guidance for selecting compatible freshwater fish, shrimp, snails, and aquatic plants based on your aquarium goals." },
  { icon: FlaskConical, title: "Water Quality Management", body: "Complete testing, water changes, RODI water preparation, conditioning, and nutrient balancing for long-term stability." },
];

const SETUP = [
  { icon: Maximize, title: "Proper Aquarium Size", body: "Choosing the correct aquarium dimensions creates greater water stability and a healthier environment." },
  { icon: Waves, title: "Stable Water Movement", body: "Balanced circulation improves oxygenation, filtration efficiency, and fish health." },
  { icon: Box, title: "Custom Stands", body: "Properly engineered aquarium stands safely support the weight of the aquarium while complementing your home's design." },
  { icon: Sprout, title: "Healthy Plant Growth", body: "Professional lighting and nutrient planning encourage lush aquatic plants that enhance both beauty and water quality." },
  { icon: Fish, title: "Safe Fish Introduction", body: "Livestock is introduced gradually to reduce stress while allowing the biological system to adapt naturally." },
];

const STATS = [
  "Years of Freshwater Experience",
  "Professionally Engineered Systems",
  "Customized Maintenance Programs",
  "Designed for Long-Term Success",
];

const GALLERY = [
  "https://images.unsplash.com/photo-1691387824643-227cc84127cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1691387896833-dba10ea7d614?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1691387747539-f681c5fa73d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1603630138578-a45a87fa97e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.pexels.com/photos/16671903/pexels-photo-16671903.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940",
  "https://images.pexels.com/photos/33445023/pexels-photo-33445023.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Bubbles = () => {
  const bubbles = useMemo(
    () =>
      Array.from({ length: 16 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 5 + Math.random() * 20,
        duration: 11 + Math.random() * 12,
        delay: Math.random() * 10,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[#34d399]/30 bg-[#34d399]/10 backdrop-blur-[1px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size }}
        />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    {[15, 45, 75].map((left, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.05, 0.18, 0.05] }}
        transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-1/3 h-[160%] w-40 rotate-12 bg-gradient-to-b from-[#7cf5c8]/40 via-[#34d399]/10 to-transparent blur-2xl"
        style={{ left: `${left}%` }}
      />
    ))}
  </div>
);

export default function FreshwaterAquariums() {
  const [phase, setPhase] = useState("intro");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("page"), TRANSITION_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const goHome = (target) => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(target);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  return (
    <main data-testid="freshwater-page" className="relative min-h-screen bg-[var(--ink)] text-[var(--foam)]">
      {phase === "intro" && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)] cursor-pointer" data-testid="freshwater-intro" onClick={() => setPhase("page")}>
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src="/media/freshwater-transition.mp4"
            autoPlay
            muted
            playsInline
            onEnded={() => setPhase("page")}
            data-testid="freshwater-transition-video"
          />
        </div>
      )}

      {/* Fixed looping background */}
      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/freshwater-loop.mp4"
          autoPlay
          loop
          muted
          playsInline
          data-testid="freshwater-loop-video"
        />
        <div className="absolute inset-0 bg-[#07160f]/90" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#061a12]/94 via-[#0a1526]/85 to-[#061410]/96" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === "page" ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10"
      >
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <LightRays />
          <Bubbles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#34d399]/40 bg-[#34d399]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7cf5c8]" data-testid="freshwater-kicker">
                Living Aquatic Artistry
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="freshwater-headline">
                Custom Freshwater<br />Aquariums
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[var(--foam-dim)] lg:text-lg">
                Beautifully designed freshwater ecosystems that combine natural artistry with
                professional engineering. From custom installations to long-term maintenance, Seatech
                creates aquariums built to thrive for years.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="freshwater-hero-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#34d399] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#05221a] transition-all duration-300 hover:shadow-[0_0_40px_-8px_#34d399]"
                >
                  Schedule a Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="freshwater-hero-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  Explore Our Process
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* WHY FRESHWATER */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Freshwater?</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {WHY.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.12}>
                  <div
                    className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#0a1a14]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#34d399]/45"
                    data-testid={`freshwater-why-card-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#34d399]/25 bg-[#34d399]/10 text-[#7cf5c8] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#34d399]">
                      <c.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-xl font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* THE SEATECH STANDARD */}
        <section id="process" className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7cf5c8]">Our Process</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">The Seatech Standard</h2>
            </Reveal>
            <div className="relative mt-14">
              <div className="absolute left-7 top-0 bottom-0 hidden w-px bg-gradient-to-b from-[#34d399]/50 via-[var(--foam)]/15 to-transparent md:block" />
              <div className="space-y-6">
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.05}>
                    <div
                      className="group relative flex items-start gap-6 rounded-3xl border border-[var(--foam)]/12 bg-[#0a1a14]/45 p-6 backdrop-blur-2xl transition-all duration-500 hover:border-[#34d399]/45 md:pl-8"
                      data-testid={`freshwater-step-${i}`}
                    >
                      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#34d399]/30 bg-[#34d399]/10 text-[#7cf5c8] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#34d399]">
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-[#7cf5c8]">Step {i + 1}</span>
                        <h3 className="mt-1 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                        <p className="mt-2 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FRESHWATER SERVICES */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Freshwater Services</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICES.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    className="group relative h-full overflow-hidden rounded-3xl border border-[var(--foam)]/12 bg-[#0a1a14]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#34d399]/45 hover:shadow-[0_20px_60px_-30px_#34d399]"
                    data-testid={`freshwater-service-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#34d399]/25 bg-[#34d399]/10 text-[#7cf5c8]">
                      <s.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                    <ChevronRight className="mt-5 h-5 w-5 text-[#7cf5c8] opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHY PROFESSIONAL SETUP MATTERS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <h2 className="text-center font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Professional Setup Matters</h2>
            </Reveal>
            <div className="mt-14 space-y-6">
              {SETUP.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.06}>
                  <div
                    className={`flex flex-col items-center gap-6 rounded-3xl border border-[var(--foam)]/12 bg-[#0a1a14]/40 p-8 backdrop-blur-2xl transition-all duration-500 hover:border-[#34d399]/40 sm:flex-row ${i % 2 === 1 ? "sm:flex-row-reverse sm:text-right" : ""}`}
                    data-testid={`freshwater-setup-${i}`}
                  >
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-[#34d399]/30 bg-[#34d399]/10 text-[#7cf5c8]">
                      <s.icon className="h-8 w-8" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="font-title text-xl font-bold tracking-tight">{s.title}</h3>
                      <p className="mt-2 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* NATURE GALLERY */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl">Nature Gallery</h2>
              <p className="mt-5 max-w-2xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                Lush planted aquariums, natural driftwood layouts, aquascaped displays, and vibrant
                community tanks, each one a living work of art.
              </p>
            </Reveal>
            <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
              {GALLERY.map((src, i) => (
                <Reveal key={i} delay={(i % 3) * 0.1} className="mb-5 break-inside-avoid">
                  <div
                    className="group relative overflow-hidden rounded-3xl border border-[var(--foam)]/12"
                    data-testid={`freshwater-gallery-${i}`}
                  >
                    <img
                      src={src}
                      alt="Planted freshwater aquarium"
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#061410]/70 via-transparent to-transparent" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CLIENTS CHOOSE SEATECH */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Clients Choose Seatech</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STATS.map((s, i) => (
                <Reveal key={s} delay={i * 0.1}>
                  <div
                    className="flex h-full items-start gap-4 rounded-3xl border border-[var(--foam)]/12 bg-[#0a1a14]/45 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#34d399]/45"
                    data-testid={`freshwater-stat-${i}`}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#34d399]/30 bg-[#34d399]/10 text-[#7cf5c8]">
                      <Check className="h-5 w-5" />
                    </div>
                    <span className="font-title text-base font-bold leading-snug tracking-tight">{s}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#04130d]/70 to-[#04130d]/90" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="freshwater-final-heading">
                Bring Nature Into Your Home
              </h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                Whether you're starting your first freshwater aquarium or upgrading an existing display,
                Seatech Aquariums combines expert planning, beautiful aquascaping, and professional
                maintenance to create thriving aquatic ecosystems you'll enjoy for years to come.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="freshwater-final-cta"
                  className="inline-flex items-center gap-3 rounded-full bg-[#34d399] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#05221a] transition-all duration-300 hover:shadow-[0_0_44px_-8px_#34d399]"
                >
                  Book Your Consultation
                </button>
                <button
                  onClick={() => goHome("#about")}
                  data-testid="freshwater-final-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  Explore Aquarium Maintenance
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </motion.div>
    </main>
  );
}
